import { useEffect, useRef, useSyncExternalStore } from 'react';
import settings from '../config/settings.json';
import { checkBackendNow, isBackendDown, subscribeBackendStatus } from '../lib/backendStatus';

/**
 * Full-screen "back in a few minutes" page shown while the database is unreachable, such as during
 * a compute resize or a Supabase incident. The app shell is served by Vercel and still loads, so
 * without this page every data call would fail on its own with scattered errors and spinners.
 *
 * It appears and clears by itself (see `lib/backendStatus`). Recovery reloads the page, so auth
 * and role checks that failed during the outage run again from a clean start.
 */
export function BackendOutageScreen() {
  const down = useSyncExternalStore(subscribeBackendStatus, isBackendDown);
  const wasDown = useRef(false);

  useEffect(() => {
    if (down) {
      wasDown.current = true;
    } else if (wasDown.current) {
      window.location.reload();
    }
  }, [down]);

  if (!down) return null;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="backend-outage-title"
      aria-describedby="backend-outage-body"
      className="fixed inset-0 z-[10000] flex items-center justify-center overflow-y-auto bg-gradient-to-b from-sky-50 to-white px-4 py-8"
    >
      <div className="w-full max-w-md text-center">
        <HamsterWheel />

        <h1 id="backend-outage-title" className="mt-6 text-2xl font-semibold text-gray-900">
          Quick maintenance in progress
          <span className="mt-1 block text-lg font-medium text-gray-600">系统维护中</span>
        </h1>

        <div id="backend-outage-body" className="mt-4 space-y-2 text-gray-700">
          <p>
            We're giving the portal a quick tune-up. It usually takes just a few minutes, and this
            page will refresh on its own when we're back.
          </p>
          <p>我们正在为系统做一次小保养，通常几分钟就好。恢复后页面会自动刷新。</p>
        </div>

        <button
          type="button"
          onClick={() => void checkBackendNow()}
          className="mt-6 rounded-full bg-sky-600 px-5 py-2 text-sm font-medium text-white shadow-sm hover:bg-sky-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2"
        >
          Check again · 重新检查
        </button>

        <p className="mt-6 text-xs text-gray-500">
          Your information is safe. Questions?{' '}
          <a href={`mailto:${settings.supportEmail}`} className="underline">
            {settings.supportEmail}
          </a>
        </p>
      </div>
    </div>
  );
}

/** A hamster jogging in its wheel. Animation classes live in index.css (off for reduced motion). */
function HamsterWheel() {
  return (
    <svg viewBox="0 0 160 170" className="mx-auto h-40 w-40" aria-hidden="true">
      <path
        d="M80 80 L48 160 M80 80 L112 160 M34 160 H126"
        stroke="#94a3b8"
        strokeWidth="6"
        strokeLinecap="round"
        fill="none"
      />
      <g className="maintenance-wheel">
        <circle cx="80" cy="80" r="62" fill="none" stroke="#38bdf8" strokeWidth="5" />
        <circle cx="80" cy="80" r="54" fill="none" stroke="#bae6fd" strokeWidth="2" strokeDasharray="4 6" />
        {[0, 60, 120].map((deg) => (
          <line
            key={deg}
            x1="80"
            y1="18"
            x2="80"
            y2="142"
            stroke="#7dd3fc"
            strokeWidth="3"
            transform={`rotate(${deg} 80 80)`}
          />
        ))}
        <circle cx="80" cy="80" r="6" fill="#0ea5e9" />
      </g>
      <g className="maintenance-runner">
        <circle cx="60" cy="122" r="3" fill="#f6b26b" />
        <ellipse cx="80" cy="124" rx="20" ry="13" fill="#f6b26b" />
        <ellipse cx="84" cy="128" rx="11" ry="7" fill="#fde7c8" />
        <circle cx="96" cy="116" r="10" fill="#f6b26b" />
        <circle cx="91" cy="107" r="4.5" fill="#f6b26b" />
        <circle cx="91" cy="107" r="2.2" fill="#f9a8d4" />
        <circle cx="100" cy="114" r="1.8" fill="#1f2937" />
        <circle cx="106" cy="118" r="1.6" fill="#f472b6" />
      </g>
      <ellipse className="maintenance-foot-a" cx="72" cy="137" rx="4.5" ry="2.5" fill="#e69138" />
      <ellipse className="maintenance-foot-b" cx="90" cy="137" rx="4.5" ry="2.5" fill="#e69138" />
    </svg>
  );
}
